import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { useAuth } from "../../../context/AuthContext";
import { useAiContext } from "../../../ai/AiContext";
import { generateDocCode, generateLegacyContractNo } from "../../../utils/idGenerator";
import { createContract, updateContract } from "../../../services/contractService";
import { uploadContractFiles, uploadTempFile } from "../../../services/storageUpload";
import { DateField } from "../../../components/form/DateField";
import { formatDate, parseDate } from "../../../utils/dateUtils";
import type { ContractDoc } from "../../../types/legalOps";
import { serverTimestamp } from "../../../../firebaseClient";
import type { AttachmentMeta } from "../../../types/attachments";
import { logLegalActivityOnChange } from "../../../services/legalActivityLogger";
import { CONTRACT_TYPE_OPTIONS } from "../../../constants/contractTypes";
import { useToast } from "../../../components/Toast";
import { AiFieldHeader } from "../../../components/shared/AiFieldHeader";
import { analyzeUploadedFile } from "../../../services/aiService";

interface Props {
  initialData: any | null;
  onClose: () => void;
  onSuccess: () => void;
  allRows: any[]; 
  isLegacyMode?: boolean; 
}

export function ContractForm({ initialData, onClose, onSuccess, allRows, isLegacyMode }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { setDraft } = useAiContext();

  const [form, setForm] = useState<Partial<ContractDoc>>(initialData || { 
      status: isLegacyMode ? "草稿" : "草稿", 
      riskLevel: "medium", 
      attachments: [], 
      relatedContractNos: [], 
      contractType: "",
      currency: "TWD",
      totalAmount: undefined,
      milestones: [],
      reviewCode: "",
      sealRequestCode: "",
      archiveCode: "",
      isLegacy: isLegacyMode,
      legacySource: ""
  });
  
  const [nextImportantDate, setNextImportantDate] = useState<string | null>(
      initialData?.nextImportantDate ?? null
  );
  
  const [amountInput, setAmountInput] = useState(initialData?.totalAmount ? String(initialData.totalAmount) : "");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiFilling, setAiFilling] = useState(false);

  const handleChange = (k: keyof ContractDoc, v: any) => setForm(p => ({ ...p, [k]: v }));
  
  useEffect(() => {
      if (!initialData && !form.contractNo) {
          let newId;
          if (isLegacyMode) {
              newId = generateLegacyContractNo(allRows);
          } else {
              newId = generateDocCode("contract", form.signDate, allRows);
          }
          handleChange("contractNo", newId);
      }
  }, [initialData, isLegacyMode]);

  const handleRegenerateNo = () => {
      let newId;
      if (isLegacyMode) {
          newId = generateLegacyContractNo(allRows);
      } else {
          newId = generateDocCode("contract", form.signDate || new Date(), allRows);
      }
      handleChange("contractNo", newId);
  };
  
  const handleAiFillFromAttachment = async () => {
    const hasPending = pendingFiles.length > 0;
    const hasExisting = form.attachments && form.attachments.length > 0;

    if (!hasPending && !hasExisting) {
        showToast("請先上傳合約檔案，再使用 AI 解析。", "error");
        return;
    }

    setAiFilling(true);
    try {
      let targetMeta: AttachmentMeta | undefined = undefined;

      if (hasPending) {
          targetMeta = await uploadTempFile(pendingFiles[0]);
      } else {
          targetMeta = form.attachments![0];
      }

      if (!targetMeta) throw new Error("檔案處理失敗");

      // Uses the new callable implementation under the hood
      const result = await analyzeUploadedFile({
          fileMeta: targetMeta,
          purpose: isLegacyMode ? "legacy_contract" : "contract_archive",
          extraContext: { title: form.title }
      });

      setForm(prev => {
        const next = { ...prev };
        if (!prev.title && result.contractTitle) next.title = result.contractTitle;
        if (!prev.counterparty && result.counterpartyName) next.counterparty = result.counterpartyName;
        
        if (result.term?.startDate) next.periodStart = result.term.startDate;
        if (result.term?.endDate) next.periodEnd = result.term.endDate;
        
        if (result.totalAmount?.value) {
            setAmountInput(String(result.totalAmount.value));
            if (result.totalAmount.currency) next.currency = result.totalAmount.currency;
        }

        const summary = result.summary || result.description;
        if (summary) {
            const oldNotes = prev.notes || "";
            if (!oldNotes.includes("【AI 摘要】")) {
                next.notes = oldNotes 
                    ? `${oldNotes}\n\n【AI 摘要】\n${summary}`
                    : `【AI 摘要】\n${summary}`;
            }
        }
        return next;
      });

      showToast("AI 解析完成", "success");

    } catch (error: any) {
      console.error("AI Error:", error);
      showToast("AI 解析失敗: " + error.message, "error");
    } finally {
      setAiFilling(false);
    }
  };

  const handleSubmit = async (targetStatus?: string) => {
    if (!form.title || !form.contractNo) {
      alert("請填寫必要欄位 (標題、編號)");
      return;
    }
    setIsSubmitting(true);
    try {
        const totalAmountVal = amountInput && !isNaN(Number(amountInput)) ? Number(amountInput) : null;
        const payload: any = { 
            ...form, 
            totalAmount: totalAmountVal,
            nextImportantDate: nextImportantDate || null,
            lastUpdatedBy: user?.displayName || "User", 
            lastUpdatedAt: serverTimestamp(),
            isLegacy: isLegacyMode || form.isLegacy
        };
        if (targetStatus) payload.status = targetStatus;

        delete payload.id;
        
        let contractId = initialData?.id;
        if (contractId) {
            await updateContract(contractId, payload);
        } else {
            contractId = await createContract(payload as ContractDoc);
        }

        if (pendingFiles.length > 0 && contractId) {
            const uploadedMetas = await uploadContractFiles(contractId, pendingFiles);
            const finalAttachments = [...(form.attachments || []), ...uploadedMetas];
            await updateContract(contractId, { attachments: finalAttachments });
        }

        await logLegalActivityOnChange({
            module: "合約",
            docId: contractId || "unknown",
            title: payload.title || "",
            action: initialData ? "修改" : "新增",
            userEmail: user?.email || "unknown"
        });

        showToast("儲存成功");
        onSuccess();
    } catch (e: any) {
        console.error(e);
        showToast("儲存失敗: " + e.message, "error");
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Modal title={initialData ? "編輯合約" : "新增合約"} onClose={onClose} width={800}>
      <div className="grid">
        <div className="field">
            <label className="label">合約編號</label>
            <div style={{ display: "flex", gap: 8 }}>
                <input className="input" value={form.contractNo||""} readOnly style={{background: 'rgba(255,255,255,0.05)', color: '#fbbf24'}} />
                {!initialData && <button className="smallbtn" onClick={handleRegenerateNo}>↻</button>}
            </div>
        </div>
        <div className="field"><label className="label">標題</label><input className="input" value={form.title||""} onChange={e => handleChange("title", e.target.value)} /></div>
        <div className="field"><label className="label">對造</label><input className="input" value={form.counterparty||""} onChange={e => handleChange("counterparty", e.target.value)} /></div>
        
        <div className="field">
            <label className="label">金額</label>
            <div style={{display:'flex', gap:8}}>
                <select className="input" style={{width:80}} value={form.currency} onChange={e=>handleChange("currency", e.target.value)}><option>TWD</option><option>USD</option></select>
                <input className="input" type="number" value={amountInput} onChange={e=>setAmountInput(e.target.value)} />
            </div>
        </div>

        <DateField label="起日" value={parseDate(form.periodStart||null)} onChange={d => handleChange("periodStart", formatDate(d))} />
        <DateField label="訖日" value={parseDate(form.periodEnd||null)} onChange={d => handleChange("periodEnd", formatDate(d))} />

        <div className="field" style={{ gridColumn: "1 / -1" }}>
            <AiFieldHeader 
                label="備註 / 摘要 (AI 支援)"
                loading={aiFilling}
                onClick={handleAiFillFromAttachment}
                disabled={pendingFiles.length === 0 && (!form.attachments || form.attachments.length === 0)}
            />
            <textarea className="input" rows={6} value={form.notes||""} onChange={e => handleChange("notes", e.target.value)} placeholder="AI 摘要將顯示於此..." />
        </div>
      </div>
      
      <FileUploadKit 
        label="合約檔案"
        existingFiles={form.attachments || []} 
        rawFiles={pendingFiles}
        onFilesSelected={(fs) => setPendingFiles(prev => [...prev, ...fs])}
        onRemoveRawFile={(idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
      />

      <div className="row" style={{ marginTop: 20, justifyContent: "flex-end", gap: 10 }}>
        <button className="btn" disabled={isSubmitting} onClick={() => handleSubmit(form.status)}>儲存</button>
      </div>
    </Modal>
  );
}
